import React from 'react';
import { BookOpen, Users, Send } from 'lucide-react';
import useSuperAdminMyTeamStore from '@/stores/SuperAdminStore/SuperAdminMyTeamStore';
import './AssignTrainingTab.css';

interface AssignTrainingTabProps {
  selectedMembers: string[];
  selectedCourses: string[];
  dueDate: string;
  sendNotification: boolean;
  onToggleMemberSelection: (id: string) => void;
  onToggleCourseSelection: (id: string) => void;
  onSetDueDate: (date: string) => void;
  onSetSendNotification: (send: boolean) => void;
  onAssignTraining: () => void;
}

const AssignTrainingTab: React.FC<AssignTrainingTabProps> = ({
  selectedMembers,
  selectedCourses,
  dueDate,
  sendNotification,
  onToggleMemberSelection,
  onToggleCourseSelection,
  onSetDueDate,
  onSetSendNotification,
  onAssignTraining
}) => {
  const { teamMembers, courses } = useSuperAdminMyTeamStore();
  return (
    <div className="admin-my-team-assign">
      <div className="admin-my-team-assign-header">
        <h2 className="admin-my-team-section-title">Assign Training</h2>
        <p className="admin-my-team-section-subtitle">Assign courses to your team members</p>
      </div>

      <div className="admin-my-team-assign-grid">
        <div className="admin-my-team-assign-card">
          <h3 className="admin-my-team-assign-card-title">Select Team Members</h3>
          <input
            type="text"
            className="admin-my-team-search-input"
            placeholder="Search team members..."
          />
          <div className="admin-my-team-member-list">
            {teamMembers?.map(member => (
              <div
                key={member.id}
                className={`admin-my-team-member-item ${
                  selectedMembers.includes(member.id) ? 'admin-my-team-member-selected' : ''
                }`}
                onClick={() => onToggleMemberSelection(member.id)}
              >
                <div className="admin-my-team-avatar admin-my-team-avatar-small">{member.initials}</div>
                <div>
                  <div className="admin-my-team-member-name">{member.name}</div>
                  <div className="admin-my-team-member-role">{member.jobTitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-my-team-assign-card">
          <h3 className="admin-my-team-assign-card-title">Select Courses</h3>
          <input
            type="text"
            className="admin-my-team-search-input"
            placeholder="Search courses..."
          />
          <div className="admin-my-team-course-list">
            {courses?.map(course => (
              <div
                key={course.id}
                className={`admin-my-team-course-item ${
                  selectedCourses.includes(course.id) ? 'admin-my-team-course-selected' : ''
                }`}
                onClick={() => onToggleCourseSelection(course.id)}
              >
                <div className="admin-my-team-course-icon">
                  <BookOpen size={20} />
                </div>
                <div>
                  <div className="admin-my-team-course-name">{course.name}</div>
                  <div className="admin-my-team-course-meta">{course.duration} • {course.lessons} lessons</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-my-team-assign-settings">
        <h3 className="admin-my-team-assign-card-title">Assignment Settings</h3>
        <div className="admin-my-team-settings-grid">
          <div className="admin-my-team-setting-field">
            <label>Due Date (Optional)</label>
            <input
              type="date"
              className="admin-my-team-date-input"
              value={dueDate}
              onChange={(e) => onSetDueDate(e.target.value)}
            />
          </div>
          <div className="admin-my-team-setting-field">
            <label>Notification</label>
            <label className="admin-my-team-checkbox-label">
              <input
                type="checkbox"
                checked={sendNotification}
                onChange={(e) => onSetSendNotification(e.target.checked)}
              />
              <span>Send email notification to users</span>
            </label>
          </div>
        </div>
      </div>

      <div className="admin-my-team-assign-summary">
        <div className="admin-my-team-summary-info">
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={16} /> {selectedMembers.length} users selected
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BookOpen size={16} /> {selectedCourses.length} courses selected
          </span>
        </div>
        <button
          className="admin-my-team-assign-btn"
          onClick={onAssignTraining}
          disabled={selectedMembers.length === 0 || selectedCourses.length === 0}
        >
          <Send size={16} /> Assign Training
        </button>
      </div>
    </div>
  );
};

export default AssignTrainingTab;
